import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { ShieldCheck, Lock, User, Eye, EyeOff, ArrowLeft, AlertCircle, Loader2 } from "lucide-react";
import API_BASE from "../config/api";

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  background: radial-gradient(circle at 50% 25%, #182235 0%, #0b0f19 75%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow-x: hidden;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  &::before {
    content: "";
    position: absolute;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 400px;
    background: radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #9ca3af;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    color: #f59e0b;
    transform: translateX(-3px);
  }

  @media (max-width: 640px) {
    position: static;
    margin-bottom: 1.25rem;
    align-self: flex-start;
  }
`;

const LoginCard = styled.div`
  background: #111827;
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 18px;
  width: 100%;
  max-width: 420px;
  padding: 2.5rem 2rem;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 0 0 25px rgba(245, 158, 11, 0.08);
  position: relative;
  z-index: 10;
  backdrop-filter: blur(12px);

  @media (max-width: 480px) {
    padding: 1.75rem 1.25rem;
    border-radius: 14px;
  }
`;

const LogoWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  text-align: center;
`;

const LogoImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  object-fit: cover;
  border: 2px solid rgba(245, 158, 11, 0.5);
  box-shadow: 0 0 20px rgba(245, 158, 11, 0.3);
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: 1.45rem;
  font-weight: 800;
  margin: 0 0 0.35rem 0;
  background: linear-gradient(135deg, #fcd34d, #f59e0b, #d97706);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.5px;
`;

const PortalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const InputGroup = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.45rem;
  }
`;

const InputFieldWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .icon-left {
    position: absolute;
    left: 0.95rem;
    color: #6b7280;
    pointer-events: none;
  }

  .toggle-right {
    position: absolute;
    right: 0.85rem;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 2px;
    &:hover {
      color: #f3f4f6;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 10px;
  padding: 0.75rem 2.5rem 0.75rem 2.6rem;
  color: #f3f4f6;
  font-size: 0.92rem;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18);
  }

  &::placeholder {
    color: #4b5563;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
  color: #f87171;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #0b0f19;
  font-weight: 700;
  font-size: 0.95rem;
  border: none;
  border-radius: 10px;
  padding: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #fbbf24, #b45309);
    box-shadow: 0 6px 20px rgba(245, 158, 11, 0.35);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    100% { transform: rotate(360deg); }
  }
`;

const SecurityFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 1.75rem;
  font-size: 0.75rem;
  color: #6b7280;
`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE}/api/admin/login`, {
        username,
        password,
      });

      if (res.data && res.data.admin && res.data.token) {
        localStorage.setItem("admin", JSON.stringify(res.data.admin));
        localStorage.setItem("adminToken", res.data.token);
        navigate("/admin");
      } else {
        setError("Authentication failed. Please verify credentials.");
      }
    } catch (err) {
      console.error("Login error:", err.message);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Invalid username or password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <BackLink to="/">
        <ArrowLeft size={16} />
        Back to Live Market
      </BackLink>

      <LoginCard>
        <LogoWrap>
          <LogoImage
            src="/assets/logo.jpeg"
            alt="GJ Bullion"
            onError={(e) => (e.target.style.display = "none")}
          />
          <Title>GJ BULLION</Title>
          <PortalBadge>
            <ShieldCheck size={12} />
            Super Admin Terminal
          </PortalBadge>
        </LogoWrap>

        {error && (
          <ErrorBanner>
            <AlertCircle size={16} />
            <span>{error}</span>
          </ErrorBanner>
        )}

        <form onSubmit={handleLogin}>
          <InputGroup>
            <label>Admin Username</label>
            <InputFieldWrapper>
              <User size={16} className="icon-left" />
              <Input
                type="text"
                placeholder="Enter admin username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </InputFieldWrapper>
          </InputGroup>

          <InputGroup>
            <label>Master Password</label>
            <InputFieldWrapper>
              <Lock size={16} className="icon-left" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="toggle-right"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </InputFieldWrapper>
          </InputGroup>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 size={17} className="spinning" />
                Verifying Access...
              </>
            ) : (
              <>
                <ShieldCheck size={17} />
                Access Control Panel
              </>
            )}
          </SubmitButton>
        </form>

        <SecurityFooter>
          <ShieldCheck size={13} color="#10b981" />
          <span>256-Bit Encrypted Secure Session</span>
        </SecurityFooter>
      </LoginCard>
    </PageWrapper>
  );
};

export default AdminLogin;
